import React, { SetStateAction, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MdOutlinePendingActions, MdSend } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { AccessRightEnum } from '../../../../../../../enum/AccessRightEnum';
import { User } from '../../../../../../../model/user/User';
import { CustomImageHandler } from '../../../../../../../shared/components/CustomImageHandler';
import MentionInputField from '../../../../../../../shared/components/message/MentionInputField';
import { formatDate, hasAccessRights } from '../../../../../../../shared/functions/Functions';
import { useSearchQuery } from '../../../../../../../shared/hooks/useSearchQuery';
import { IAllStates } from '../../../../../../../store/rootReducer';
import { ErrorToast, SuccessToast, WarningToast } from '../../../../../../../utils/toasters';
import { ManageService } from '../../../service';
import { iManageList } from '../../dto/IManageCalendar';
import CalendarManageLeftCard from './CalendarManageLeftCard';
import CalendarManageRightCard from './CalendarManageRightCard';

interface iCalendarModalBodyProps {
    state: iManageList;
    setState: React.Dispatch<SetStateAction<iManageList>>;
    setHasTextInsideField: React.Dispatch<SetStateAction<boolean>>;
    changeCaptionHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CalendarModalBody = ({
    state,
    setState,
    setHasTextInsideField,
    changeCaptionHandler,
}: iCalendarModalBodyProps) => {
    const [possibleMentions, setPossibleMentions] = useState<User[]>([]);
    const { t } = useTranslation();
    const [currTab, setCurrTab] = useState<number>(0);
    const [isSendDisabled, setIsSendDisabled] = useState(false);
    const textInput = document.getElementById('chat-input') as HTMLInputElement;
    const { user } = useSelector((states: IAllStates) => states.auth);
    const [newMessage, setNewMessage] = useState({
        message: '',
    });
    const hasClients = useSearchQuery('hasClients');
    const lastMessageRef = useRef<HTMLInputElement>(null);
    const showSendInput =
        !hasAccessRights(AccessRightEnum.INFLUENCER_FEEDBACK_HIDE) ||
        hasAccessRights(AccessRightEnum.MANAGE_CAN_VIEW_CLIENT_COMMENTS);

    const isProposed = state?.status === 'proposed';
    const showInfluencerFeedback =
        state?.showInfluencerComments ?? !hasAccessRights(AccessRightEnum.INFLUENCER_FEEDBACK_HIDE);
    const name = state?.offerItem?.offer?.user?.displayName ?? '';

    const changeHandler = (name: string, value: string) => {
        setNewMessage({ ...newMessage, [name]: value });
    };

    const sendMessageHandler = () => {
        if (!newMessage?.message?.trim()?.length) {
            return WarningToast(t('general.toasts.warnEmptyMessage'));
        }
        setIsSendDisabled(true);
        currTab === 0
            ? ManageService.createNewComment({
                  ...newMessage,
                  eventId: state?.id,
                  message: newMessage.message,
              })
                  .then(response => {
                      setState(response);
                      textInput.value = '';
                      setNewMessage({ message: '' });
                  })
                  .catch(error => ErrorToast(error))
                  .finally(() => setIsSendDisabled(false))
            : ManageService.createClientComment({
                  ...newMessage,
                  eventId: state?.id,
                  message: newMessage.message,
              })
                  .then(response => {
                      setState(response);
                      textInput.value = '';
                      setNewMessage({ message: '' });
                  })
                  .catch(error => ErrorToast(error))
                  .finally(() => setIsSendDisabled(false));
    };
    const sendMessagePressingEnter = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (event.key === 'Enter') {
            sendMessageHandler();
        }
    };

    useEffect(() => {
        if (!lastMessageRef.current) return;
        if (!!state?.comments?.length || !!state?.clientComments?.length) {
            lastMessageRef.current.scrollTop = lastMessageRef.current.scrollHeight;
        }
    }, [state?.comments?.length, state?.clientComments?.length]);

    useEffect(() => {
        setHasTextInsideField(!!newMessage?.message?.length);
    }, [newMessage?.message?.length]);

    useEffect(() => {
        !!state?.offerItem?.offer?.campaign?.id &&
            !!state?.id &&
            ManageService.getMentionParticipants(
                state?.offerItem?.offer?.campaign?.id,
                !!currTab,
                false,
                true,
                state?.id
            )
                .then(response => {
                    setPossibleMentions(response?.data);
                })
                .catch(err => ErrorToast(err));
    }, [state?.offerItem?.offer?.campaign?.id, state?.id, currTab]);

    const handleCopy = (value: string) => {
        SuccessToast(t('general.button.copied'));
        navigator.clipboard.writeText(value);
    };

    useEffect(() => {
        setCurrTab(showInfluencerFeedback ? Number(hasClients) || 0 : 1);
    }, [hasClients, showInfluencerFeedback, JSON.stringify(user?.accessRights)]);

    return (
        <Row>
            <Col className="max-h-70vh-lg overflow-y-auto global" md={4}>
                <CalendarManageLeftCard state={state} setState={setState} handleCopy={handleCopy} />
            </Col>
            {isProposed ? (
                <Col md={8} className="d-flex flex-column align-items-center justify-content-center text-center p-5">
                    <img src="/icons/being-verified-icon.svg" alt="Being verified icon" height={90} />
                    <h6 className="mt-4 mb-2">{t('brand.campaign.proposed_pending_title')}</h6>
                    <p className="text-muted mb-0 mx-auto" style={{ maxWidth: '420px' }}>
                        {t('brand.campaign.proposed_pending', {
                            proposedDate: state.postingDate ? formatDate(state.postingDate) : '-',
                            influencerDisplayName: name ?? '-',
                        })}
                    </p>
                </Col>
            ) : (
                <Col md={8} className="max-h-70vh-lg overflow-auto">
                    <CalendarManageRightCard
                        state={state}
                        setState={setState}
                        lastMessageRef={lastMessageRef}
                        handleCopy={handleCopy}
                        currTab={currTab}
                        setCurrTab={setCurrTab}
                        changeCaptionHandler={changeCaptionHandler}
                    />
                    {showSendInput && (
                        <div className="border-bottom d-flex align-items-center pb-2 w-100">
                            <CustomImageHandler
                                altTag="User Profile"
                                thumbnailPath={user?.profilePhoto?.thumbnail as string}
                                photoPath={user?.profilePhoto?.path as string}
                                classes="user-img rounded-circle"
                                name={user?.displayName || user?.firstName}
                            />
                            <div className="w-90 ml-2">
                                <MentionInputField
                                    value={newMessage?.message}
                                    possibleMentions={possibleMentions}
                                    id={'chat-input'}
                                    changeMessageHandler={val => changeHandler('message', val.target.value)}
                                    sendMessage={sendMessagePressingEnter}
                                />
                            </div>
                            <MdSend
                                className={`chat-input-icon ml-2 send ${isSendDisabled ? 'opacity-25' : ''}`}
                                onClick={() => !isSendDisabled && sendMessageHandler()}
                            />
                        </div>
                    )}
                </Col>
            )}
        </Row>
    );
};
